/**
 * Incident Actions for Customer Confidence Call
 * Handles API calls and utilities related to incident data
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getDataFromSessionStorage } from "../../../config/configHelper";
import { HttpService } from "../../../library/http/http.service";
const ALLOWED_ORDER_BY_FIELDS = new Set([
    "title",
    "status",
    "severity",
    "impactStartTime",
    "icmIncidentId",
]);
const normalizeOrderBy = (orderBy) => {
    const fallback = "impactStartTime desc";
    if (!(orderBy === null || orderBy === void 0 ? void 0 : orderBy.trim())) {
        return fallback;
    }
    const [field, direction] = orderBy.trim().split(/\s+/, 2);
    const normalizedDirection = (direction || "desc").toLowerCase();
    if (!ALLOWED_ORDER_BY_FIELDS.has(field) ||
        (normalizedDirection !== "asc" && normalizedDirection !== "desc")) {
        return fallback;
    }
    return `${field} ${normalizedDirection}`;
};
/**
 * Map a backend OutageItem to the UI IncidentData model.
 */
const mapOutageItemToIncidentData = (item) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    return ({
        icmNumber: String(item.icmIncidentId),
        isOutage: true,
        status: (_a = item.status) !== null && _a !== void 0 ? _a : "Unknown",
        impactType: (_b = item.impactType) !== null && _b !== void 0 ? _b : "Outage",
        impactStart: (_c = item.impactStartTime) !== null && _c !== void 0 ? _c : "",
        severity: (_d = item.severity) !== null && _d !== void 0 ? _d : "",
        regionsImpacted: (_e = item.impactedRegions) !== null && _e !== void 0 ? _e : [],
        customerSubscriptionsImpacted: [],
        title: (_f = item.title) !== null && _f !== void 0 ? _f : "",
        affectedServices: (_g = item.impactedServices) !== null && _g !== void 0 ? _g : [],
        affectedProducts: ((_h = item.impactedProducts) !== null && _h !== void 0 ? _h : []).filter((product) => !!(product === null || product === void 0 ? void 0 : product.id) && !!(product === null || product === void 0 ? void 0 : product.name)),
        trackingId: (_j = item.trackingId) !== null && _j !== void 0 ? _j : "",
        childCount: (_k = item.childCount) !== null && _k !== void 0 ? _k : 0,
    });
};
/**
 * Fetch outages from the backend API for a given TPID.
 * Returns both the page of IncidentData items and the total server-side count.
 */
export const fetchOutagesByTpid = (params) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b, _c, _d;
    const { tpid, days = 28, search, page = 1, pageSize = 50, flatten = false, orderBy = "impactStartTime desc", } = params;
    const normalizedTpid = tpid === null || tpid === void 0 ? void 0 : tpid.trim();
    if (!normalizedTpid || !/^\d+$/.test(normalizedTpid)) {
        throw new Error("Invalid TPID. Expected numeric account identifier.");
    }
    const safePageSize = Math.min(200, Math.max(1, pageSize));
    const safePage = Math.max(1, page);
    const http = new HttpService();
    const baseUrl = getDataFromSessionStorage("globalFrontdoorbaseurl");
    const route = `/api/accounts/${encodeURIComponent(normalizedTpid)}/outages`;
    const queryParams = new URLSearchParams();
    const safeDays = Math.min(90, Math.max(1, days));
    const safeOrderBy = normalizeOrderBy(orderBy);
    queryParams.set("days", String(safeDays));
    queryParams.set("page", String(safePage));
    queryParams.set("pageSize", String(safePageSize));
    queryParams.set("flatten", String(flatten));
    queryParams.set("orderBy", safeOrderBy);
    if (search === null || search === void 0 ? void 0 : search.trim()) {
        queryParams.set("search", search.trim());
    }
    const response = (yield http.get(baseUrl, route, queryParams));
    const rawData = response === null || response === void 0 ? void 0 : response.data;
    const body = rawData && ("items" in rawData || "value" in rawData)
        ? rawData
        : rawData === null || rawData === void 0 ? void 0 : rawData.result;
    const outageItems = (_b = (_a = body === null || body === void 0 ? void 0 : body.items) !== null && _a !== void 0 ? _a : body === null || body === void 0 ? void 0 : body.value) !== null && _b !== void 0 ? _b : [];
    const items = outageItems.map(mapOutageItemToIncidentData);
    const totalCount = (_d = (_c = body === null || body === void 0 ? void 0 : body.totalCount) !== null && _c !== void 0 ? _c : body === null || body === void 0 ? void 0 : body.odataCount) !== null && _d !== void 0 ? _d : items.length;
    return {
        items,
        totalCount,
        customerName: body === null || body === void 0 ? void 0 : body.customerName,
    };
});
const buildEscalationReasonAgentPayload = (messageText) => ({
    jsonrpc: "2.0",
    id: "1",
    method: "message/send",
    params: {
        message: {
            kind: "message",
            role: "user",
            messageId: `msg-${Date.now()}`,
            parts: [
                {
                    kind: "text",
                    text: messageText,
                },
            ],
        },
    },
});
const tryParseJson = (value) => {
    if (typeof value === "object" && value !== null) {
        return value;
    }
    if (typeof value !== "string") {
        return undefined;
    }
    try {
        return JSON.parse(value);
    }
    catch (_a) {
        return undefined;
    }
};
export const fetchAzureProductSuggestions = (promptText) => __awaiter(void 0, void 0, void 0, function* () {
    var _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
    const http = new HttpService();
    const baseUrl = getDataFromSessionStorage("globalFrontdoorbaseurl");
    const route = "/api/agents/AzureProductSuggestionAgent";
    const payload = buildEscalationReasonAgentPayload(promptText);
    const response = (yield http.post(baseUrl, route, undefined, payload));
    const agentText = (_l = (_k = (_j = (_h = (_g = (_f = (_e = response === null || response === void 0 ? void 0 : response.data) === null || _e === void 0 ? void 0 : _e.result) === null || _f === void 0 ? void 0 : _f.responsePayload) === null || _g === void 0 ? void 0 : _g.result) === null || _h === void 0 ? void 0 : _h.parts) === null || _j === void 0 ? void 0 : _j[0]) === null || _k === void 0 ? void 0 : _k.text) !== null && _l !== void 0 ? _l : (_r = (_q = (_p = (_o = (_m = response === null || response === void 0 ? void 0 : response.data) === null || _m === void 0 ? void 0 : _m.responsePayload) === null || _o === void 0 ? void 0 : _o.result) === null || _p === void 0 ? void 0 : _p.parts) === null || _q === void 0 ? void 0 : _q[0]) === null || _r === void 0 ? void 0 : _r.text;
    const parsed = tryParseJson(agentText);
    const masterListCandidate = parsed === null || parsed === void 0 ? void 0 : parsed.masterList;
    const parsedMasterList = Array.isArray(masterListCandidate)
        ? masterListCandidate
        : [];
    const masterList = parsedMasterList
        .filter((item) => !!(item === null || item === void 0 ? void 0 : item.key) && !!(item === null || item === void 0 ? void 0 : item.label))
        .map((item) => ({ key: item.key, label: item.label }));
    if (masterList.length === 0) {
        throw new Error("Azure product suggestion agent returned invalid schema.");
    }
    const suggestedProductsCandidate = parsed === null || parsed === void 0 ? void 0 : parsed.suggestedProducts;
    const suggestedReasonsCandidate = parsed === null || parsed === void 0 ? void 0 : parsed.suggestedReasons;
    const suggestedRaw = Array.isArray(suggestedProductsCandidate)
        ? suggestedProductsCandidate
        : Array.isArray(suggestedReasonsCandidate)
            ? suggestedReasonsCandidate
            : [];
    const suggestedProducts = suggestedRaw
        .filter((item) => !!(item === null || item === void 0 ? void 0 : item.key) && !!(item === null || item === void 0 ? void 0 : item.label))
        .map((item) => ({ key: item.key, label: item.label }));
    return {
        masterList,
        suggestedProducts,
        summary: parsed === null || parsed === void 0 ? void 0 : parsed.summary,
    };
});
/**
 * @deprecated Use fetchOutagesByTpid instead. Kept temporarily for backward compatibility.
 */
export const fetchIncidentsByCustomerTpid = (customerTpid) => __awaiter(void 0, void 0, void 0, function* () {
    const { items } = yield fetchOutagesByTpid({
        tpid: customerTpid,
        page: 1,
        pageSize: 200,
    });
    return items;
});
/**
 * Generate ICM portal URL for an incident
 * @param icmNumber - The ICM incident number
 * @returns string - The full ICM portal URL
 */
export const generateIcmPortalUrl = (icmNumber, isOutage = false) => {
    const detailsPath = isOutage ? "outages" : "incidents";
    return `https://portal.microsofticm.com/imp/v5/${detailsPath}/details/${icmNumber}/summary`;
};
/**
 * Format date/time for display
 * @param dateTimeString - ISO date string
 * @returns string - Formatted date/time
 */
export const formatImpactStartTime = (dateTimeString) => {
    try {
        const date = new Date(dateTimeString);
        return date.toLocaleString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            timeZoneName: "short",
        });
    }
    catch (error) {
        console.error("Error formatting date:", error);
        return dateTimeString;
    }
};
/**
 * Get severity color for styling
 * @param severity - The severity level
 * @returns string - CSS color value
 */
export const getSeverityColor = (severity) => {
    switch (severity.toLowerCase()) {
        case "sev 1":
            return "#d13438"; // Red for Sev 1
        case "sev 2":
            return "#ff8c00"; // Orange for Sev 2
        case "sev 3":
        case "sev 4":
        case "sev 5":
        default:
            return "#107c10"; // Green for Sev 3 and above
    }
};
/**
 * Get status color for styling
 * @param status - The incident status
 * @returns string - CSS color value
 */
export const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
        case "active":
            return "#d13438"; // Red
        case "investigating":
            return "#ff8c00"; // Orange
        case "mitigated":
            return "#ffd700"; // Gold
        case "monitoring":
            return "#1ba1e2"; // Blue
        case "resolved":
            return "#32cd32"; // Green
        default:
            return "#666666"; // Gray
    }
};
/**
 * Get status icon for display
 * @param status - The incident status
 * @returns string - Fluent UI icon name
 */
export const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
        case "active":
            return "StatusErrorFull"; // Red circle with X
        case "investigating":
            return "Search"; // Magnifying glass
        case "mitigated":
            return "CheckMark"; // Check mark
        case "monitoring":
            return "View"; // Eye icon
        case "resolved":
            return "Completed"; // Green circle with check
        default:
            return "StatusCircleInner"; // Generic circle
    }
};
